import { useAuthStore } from '@/stores/useAuthStore';
import { useAppStore } from '@/stores/useAppStore';
import { useAccountStore } from '@/stores/useAccountStore';
import { watch } from 'vue';
import PageAboutApp from '@/views/PageAboutApp.vue';
import PageApiManager from '@/views/PageApiManager.vue';
import PageChooseProvider from '@/views/PageChooseProvider.vue';
import PageDeviceDetail from '@/views/PageDeviceDetail.vue';
import PageDevices from '@/views/PageDevices.vue';
import PageDownloads from '@/views/PageDownloads.vue';
import PageEfriends from '@/views/PageEfriends.vue';
import PageEfriendsMap from '@/views/PageEfriendsMap.vue';
import PageExperts from '@/views/PageExperts.vue';
import PageHelp from '@/views/PageHelp.vue';
import PageHistory from '@/views/PageHistory.vue';
import PageHome from '@/views/PageHome.vue';
import PageInvoices from '@/views/PageInvoices.vue';
import PageLogin from '@/views/PageLogin.vue';
import PageMeterDetail from '@/views/PageMeterDetail.vue';
import PageMyData from '@/views/PageMyData.vue';
import PageNotFound from '@/views/PageNotFound.vue';
import PageNotifications from '@/views/PageNotifications.vue';
import PagePasswordReset from '@/views/PagePasswordReset.vue';
import PageSelectCube from '@/views/PageSelectCube.vue';
import PageSetNewPassword from '@/views/PageSetNewPassword.vue';
import PageSettings from '@/views/PageSettings.vue';
import PageShop from '@/views/PageShop.vue';
import PageShopArticle from '@/views/PageShopArticle.vue';
import PageShopBooking from '@/views/PageShopBooking.vue';
import PageTour from '@/views/PageTour.vue';
import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
  history: createWebHistory(''),
  linkActiveClass: 'nav__link--active',
  routes: [
    {
      path: '/',
      name: 'home',
      component: PageHome,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: PageLogin,
      meta: {
        requiresLogout: true,
        disableLoader: true,
      },
    },
    {
      path: '/passwort-zuruecksetzen',
      name: 'password-reset',
      component: PagePasswordReset,
      meta: {
        requiresLogout: true,
        disableLoader: true,
      },
    },
    {
      path: '/passwort/:token',
      name: 'new-password',
      component: PageSetNewPassword,
      meta: {
        requiresLogout: true,
        disableLoader: true,
      },
    },
    {
      path: '/select-cube',
      name: 'select-cube',
      component: PageSelectCube,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/history',
      name: 'history',
      component: PageHistory,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/efriends',
      name: 'efriends',
      component: PageEfriends,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/efriends/map/:selected?',
      name: 'map',
      component: PageEfriendsMap,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/tour/:selected?',
      name: 'tour',
      component: PageTour,
      meta: {
        requiresAuth: true,
        hideNavigation: true,
      },
    },
    {
      path: '/settings',
      name: 'settings',
      component: PageSettings,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/my-data',
      name: 'my-data',
      component: PageMyData,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/invoices',
      name: 'invoices',
      component: PageInvoices,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/downloads',
      name: 'downloads',
      component: PageDownloads,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/experts',
      name: 'experts',
      component: PageExperts,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/about-app',
      name: 'about-app',
      component: PageAboutApp,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/help',
      name: 'help',
      component: PageHelp,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/devices',
      name: 'devices',
      component: PageDevices,
      meta: {
        requiresAuth: true,
      },
      beforeEnter: async () => {
        await waitForAppInit()
        const { useDeviceStore } = await import('@/stores/useDeviceStore')
        const deviceStore = useDeviceStore()
        if (!deviceStore.devicesLoaded) { deviceStore.getAllDevices() }
      },
    },
    {
      path: '/devices/efriends/:deviceid?/:tab?',
      name: 'device-detail',
      component: PageDeviceDetail,
      meta: {
        requiresAuth: true,
        defaultTab: 'consumption',
      },
      beforeEnter: async () => {
        await waitForAppInit()
        const { useDeviceStore } = await import('@/stores/useDeviceStore')
        const deviceStore = useDeviceStore()
        if (!deviceStore.devicesLoaded) { deviceStore.getAllDevices() }
      },
    },
    {
      path: '/meters/efriends/:meterid/:tab?',
      name: 'meter-detail',
      component: PageMeterDetail,
      meta: {
        requiresAuth: true,
        defaultTab: 'consumption',
      },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: PageNotifications,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/shop',
      name: 'shop',
      component: PageShop,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/shop/artikel/:id',
      name: 'shop-article',
      component: PageShopArticle,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/shop/buchung/:id',
      name: 'shop-booking',
      component: PageShopBooking,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/choose-provider/:context?',
      name: 'choose-provider',
      component: PageChooseProvider,
      meta: {
        requiresAuth: true,
      },
      props: true,
    },
    {
      path: '/api-manager',
      name: 'api-manager',
      component: PageApiManager,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: PageNotFound,
      meta: {
        disableLoader: true,
      },
    },
  ],
})

// Authentication Guards
router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    return requiresAuth(to, next)
  }

  if (to.matched.some((record) => record.meta.requiresLogout)) {
    requiresLogout(next)
  }

  if (to.matched.some((record) => record.meta.disableLoader)) {
    disableLoader(next)
  }

  next()
})

router.beforeEach((to, from, next) => {
  useAppStore().showNavigation = !to.matched.some((record) => record.meta.hideNavigation)
  next()
})

const routesRedirectingToChooseProvider = ['home', 'devices', 'history']
router.beforeEach((to: any, from, next: any) => {
  if (
    routesRedirectingToChooseProvider.indexOf(to.name as string) > -1 &&
    useAccountStore().isDemoUser
  ) {
    return next({
      name: 'choose-provider',
      params: {
        context: to.name,
      },
    })
  }
  return next()
})

router.beforeEach((to, from , next) => {
  useAppStore().currentRouteName = to.name as string
  next()
})

// Waits for appStore.init() to complete before proceeding.
// Required for routes that load data lazily via beforeEnter — on a hard
// navigation (direct URL or page refresh), beforeEnter fires before App.vue
// mounts and init() runs, so the auth token isn't set up yet and any API
// call made immediately would result in a 401.
function waitForAppInit(): Promise<void> {
  const appStore = useAppStore()
  if (appStore.appInitialized) { return Promise.resolve() }
  return new Promise(resolve => {
    const stop = watch(() => appStore.appInitialized, (val) => {
      if (val) { stop(); resolve() }
    })
  })
}

function requiresAuth(to, next) {
  if (!useAuthStore().userIsAuthenticated) {
    return next({ name: 'login', query: { redirect: to.path } })
  }

  return next()
}

function disableLoader(next: unknown) {
  // The loading flag is not needed on pages that don't have access to user data
  useAppStore().isLoading = false
}

function requiresLogout(next: unknown) {
  if (useAuthStore().userIsAuthenticated) {
    useAuthStore().clearAuth()
  }
}

export default router
